import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Send as SubmitIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';

const QuoteApprovalDialog = ({ open, onClose, quote, onSuccess, userRole }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  if (!quote) return null;

  // QL-010: active managers may approve too (the API already allows it).
  const canApprove = userRole === 'admin' || userRole === 'superadmin' || !!user?.is_quote_approver;
  const canSubmit = quote.status === 'draft';
  const isPending = quote.status === 'pending';
  const isAlreadyApproved = quote.status === 'approved';
  
  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await makeApiCall(`api/quotes/${quote.id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          status: newStatus,
          notes: approvalNotes || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(`Quote ${newStatus} successfully`, data.quote);
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${newStatus} quote`);
      }
    } catch (error) {
      console.error(`Error ${newStatus} quote:`, error);
      setError(`Failed to ${newStatus} quote`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Quote Approval Management
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Naomi (TKT-178) couldn't tell whether clicking Approve had
            actually worked, because for already-approved quotes the dialog
            simply hid the Approve button. This banner makes the current
            state unambiguous and points to the next step. */}
        {isAlreadyApproved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This quote is approved and ready to be added to a route. Open
            Route Planning, pick the day/truck slot, then use Add Customers
            and search for the customer or address to attach this quote.
          </Alert>
        )}

        {/* Quote Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quote #{quote.quote_number}
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="textSecondary">Customer</Typography>
              <Typography variant="body1">
                {quote.company_name || `${quote.first_name} ${quote.last_name}`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Total Amount</Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrencyWithSymbol(quote.total_amount || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Created By</Typography>
              <Typography variant="body1">
                {quote.created_by || 'Unknown'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="textSecondary">Current Status</Typography>
              <Chip 
                label={quote.status} 
                color={getStatusColor(quote.status)}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
          </Box>

          {quote.description && (
            <Box>
              <Typography variant="body2" color="textSecondary">Description</Typography>
              <Typography variant="body1">{quote.description}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Approval Notes */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Approval Notes (Optional)"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Add notes about the approval decision..."
          />
        </Box>

        {/* Workflow Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quote Workflow:
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • <strong>Draft:</strong> Quote is being created/edited<br/>
            • <strong>Pending:</strong> Submitted for manager approval<br/>
            • <strong>Approved:</strong> Ready to send to customer and create jobs<br/>
            • <strong>Rejected:</strong> Needs revision before resubmission<br/>
            • <strong>Expired:</strong> Quote has expired
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        
        {/* Submit for Approval (Sales Person) */}
        {canSubmit && (
          <Button
            variant="outlined"
            startIcon={<SubmitIcon />}
            onClick={() => handleStatusChange('pending')}
            disabled={loading}
            color="primary"
          >
            Submit for Approval
          </Button>
        )}
        
        {/* Manager Approval Actions */}
        {canApprove && isPending && (
          <>
            <Button
              variant="outlined"
              startIcon={<RejectIcon />}
              onClick={() => handleStatusChange('rejected')}
              disabled={loading}
              color="error"
            >
              Reject
            </Button>
            
            <Button
              variant="contained"
              startIcon={<ApproveIcon />}
              onClick={() => handleStatusChange('approved')}
              disabled={loading}
              color="success"
            >
              Approve Quote
            </Button>
          </>
        )}
        
        {/* Additional Status Changes for Admins. Hide redundant options
            that match the quote's current status so users don't pick
            "Approve" on an already-approved quote and assume nothing
            happened (TKT-178). */}
        {canApprove && quote.status !== 'draft' && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Change Status</InputLabel>
            <Select
              value=""
              label="Change Status"
              onChange={(e) => {
                if (e.target.value) {
                  handleStatusChange(e.target.value);
                }
              }}
              disabled={loading}
            >
              {quote.status !== 'draft' && (
                <MenuItem value="draft">Back to Draft</MenuItem>
              )}
              {quote.status !== 'pending' && (
                <MenuItem value="pending">Back to Pending</MenuItem>
              )}
              {quote.status !== 'approved' && (
                <MenuItem value="approved">Approve</MenuItem>
              )}
              {quote.status !== 'rejected' && (
                <MenuItem value="rejected">Reject</MenuItem>
              )}
              {quote.status !== 'expired' && (
                <MenuItem value="expired">Mark Expired</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuoteApprovalDialog;
